import { get, isEmpty, isFunction, map } from 'lodash-es'
import PropTypes from 'prop-types'
import React, { useCallback } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  useOverlayState,
  changeOverlay,
  removeOverlay,
} from '../../../../../firebase/hooks/useOverlays'
import { Col, Row } from '../../../../components'
import GroupedComments from '../GroupedComments/GroupedComments'
import CommentInListComponent from '../CommentInListComponent/CommentInListComponent'
import NoQuestionsComponent from '../NoQuestionsComponent/NoQuestionsComponent'

import './CommentsListComponent.scss'

const renderComment = (renderSingleComment, item, options) => {
  const { unreadIdsList } = options
  const props = {
    item,
    style: options.style,
    styles: get(options, 'styles.singleCommentStyles'),
  }

  if (renderSingleComment) {
    return renderSingleComment(props)
  }

  return (
    <CommentInListComponent
      {...props}
      isUnread={
        unreadIdsList.has(item.comment_id) ||
        unreadIdsList.has(parseInt(item.comment_id, 10))
      }
    />
  )
}

const handleEmptyState = (commentType, allowEmpty, renderCustomEmptyState) => {
  if (allowEmpty) {
    return null
  }

  if (isFunction(renderCustomEmptyState)) {
    return renderCustomEmptyState()
  }

  return (
    <div className="no-comments">
      {commentType === 'question' ? (
        <div className="no-questions-wrapper">
          <NoQuestionsComponent />
        </div>
      ) : (
        <FormattedMessage defaultMessage="No chat messages have been posted" />
      )}
    </div>
  )
}

const renderComments = (options) => {
  const {
    renderSingleComment,
    comments,
    commentType,
    allowEmpty,
    gridSize,
    renderCustomEmptyState,
  } = options

  if (isEmpty(comments)) {
    return handleEmptyState(commentType, allowEmpty, renderCustomEmptyState)
  }

  const isGrid = Number.isFinite(gridSize)
  const List = isGrid ? Row : 'ul'
  const Item = isGrid ? Col : 'li'

  return (
    <List
      className="comments-list"
      {...(isGrid ? { gutter: 16, type: 'flex' } : {})}
    >
      {map(comments, (comment) => (
        <Item
          key={comment.comment_id}
          className="comments-item"
          {...(isGrid ? { sm: gridSize } : {})}
        >
          {renderComment(renderSingleComment, comment, options)}
        </Item>
      ))}
    </List>
  )
}

const CommentsListComponent = (props) => {
  const {
    commentType,
    isGroupedByStatus,
    comments,
    moveComment,
    updateComment,
  } = props
  const overlay = useOverlayState()

  const handleCommentShowClick = useCallback(
    (comment) => {
      if (+comment.comment_id === overlay?.id) {
        removeOverlay()
      } else {
        changeOverlay({
          type: 'question',
          data: {
            name: comment.name ?? 'audience',
            text: comment.content_text,
            id: +comment.comment_id,
          },
        })
      }
    },
    [overlay]
  )

  return (
    <div className={`comments-list-component type-${commentType}`}>
      {!isGroupedByStatus ? (
        renderComments(props)
      ) : (
        <GroupedComments
          group={comments}
          onCommentMoved={moveComment}
          onCommentAnsweredClick={(comment) =>
            updateComment(comment.comment_id, 'answered')
          }
          onCommentDismissedClick={(comment) =>
            updateComment(comment.comment_id, 'dismissed')
          }
          onCommentShowClick={handleCommentShowClick}
          currentlyPresentedCommentId={overlay?.id}
        />
      )}
    </div>
  )
}

CommentsListComponent.propTypes = {
  comments: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.objectOf(PropTypes.object),
    PropTypes.object,
  ]),
  commentType: PropTypes.oneOf(['question', 'chat']),
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  renderSingleComment: PropTypes.func,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  renderCustomEmptyState: PropTypes.func,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  allowEmpty: PropTypes.bool,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  extraButtons: PropTypes.func,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  style: PropTypes.object,
  styles: PropTypes.shape({
    singleCommentStyles: PropTypes.shape({
      readCommentStyle: PropTypes.object,
      unreadCommentStyle: PropTypes.object,
    }),
  }),
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  unreadIdsList: PropTypes.object,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  gridSize: PropTypes.number,
  moveComment: PropTypes.func,
  // eslint-disable-next-line react/no-unused-prop-types
  updateComment: PropTypes.func,
  isGroupedByStatus: PropTypes.bool,
}

CommentsListComponent.defaultProps = {
  comments: [],
}

export default CommentsListComponent
